import Button from '@material-ui/core/Button/Button';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { useMemo, useState } from 'react';
import { OrganizationSuggestionsResponse } from '../../../store/signup/types';
import { minSearchCardHeight, shadow } from './SearchLoading';
import { useTranslation } from 'react-i18next';
import ClaimOrganizationModal from './ClaimOrganizationModal';
import { pushGivelifySignIn } from '../steps/SignupSubmitted/v1/ViewV1';
import { pushEvent } from '../../../utils/clevertapUtils';
import {
    CLICK_ORG_SEARCH_CLAIM_BTN,
    CLICK_ORG_SEARCH_SIGN_IN_BTN,
} from '../../../utils/clevertapEvents';

interface SearchResultProps {
    data: OrganizationSuggestionsResponse;
}

const useStyles = makeStyles((theme: Theme) => ({
    cardStyles: {
        '& *': {
            fontSize: 14,
        },
        cursor: 'pointer',
        width: '100%',
        height: '100%',
        minHeight: minSearchCardHeight,
        border: '1px solid #e3e3e3',
        borderRadius: 10,
        boxShadow: shadow,
        padding: '24px 16px 16px 16px',
        '& .already-registered': {
            color: '#6F7A87',
            paddingTop: 2,
            paddingBottom: 2,
            paddingRight: theme.spacing(3),
            paddingLeft: theme.spacing(3),
            background: 'rgba(202, 210, 219, 0.3)',
            borderRadius: 20,
        },
        '& .buttons': {
            display: 'flex',
            justifyContent: (isUnregistered: boolean) =>
                isUnregistered ? 'flex-end' : 'space-between',
            alignItems: 'center',
        },
        '& .name': {
            fontWeight: 'bold',
            marginBottom: theme.spacing(2),
        },
        '& .detail': {
            marginBottom: theme.spacing(4),
        },
        [theme.breakpoints.down('sm')]: {
            '& .detail': {
                marginBottom: '0 !important',
            },
            minHeight: 'inherit',
            padding: '16px 16px 12px 16px',
        },
    },
}));

const SearchResult: React.FC<SearchResultProps> = ({ data }) => {
    const { t } = useTranslation();
    const { source } = data;
    const isUnregistered = source.unregistered === '1';
    const { cardStyles } = useStyles(isUnregistered);
    const [open, setOpen] = useState<boolean>(false);

    const handleClose = () => setOpen(false);
    const handleOpen = () => {
        const organizationName = {
            organizationName: source.name,
            doneeId: source.doneeId,
            ypId: source.ypId,
        };
        if (isUnregistered) {
            setOpen(true);
            pushEvent(CLICK_ORG_SEARCH_CLAIM_BTN, organizationName);
        } else {
            pushEvent(CLICK_ORG_SEARCH_SIGN_IN_BTN, organizationName);
            pushGivelifySignIn();
        }
    };

    const { organizationSignupText, organizationSignIn, alreadyRegistered } =
        useMemo(
            () => ({
                alreadyRegistered: t(
                    'signup.organizationSearch.alreadyRegistered',
                ),
                organizationSignupText: t(
                    'signup.organizationSearch.organizationSignupText',
                ),
                organizationSignIn: t(
                    'signup.organizationSearch.organizationSignIn',
                ),
            }),
            [t],
        );

    return (
        <>
            <ClaimOrganizationModal
                isOpen={open}
                organization={data}
                handleClose={handleClose}
            />
            <div className={cardStyles} onClick={handleOpen}>
                <div className="detail">
                    <div className="name">{source.name}</div>
                    <div>{source.address}</div>
                    <div>
                        {source.city}, {source.state}
                    </div>
                    <div>{source.zip}</div>
                </div>
                <div className="buttons">
                    {!isUnregistered && (
                        <div className="already-registered">
                            {alreadyRegistered}
                        </div>
                    )}
                    <Button variant="text" onClick={handleOpen}>
                        {isUnregistered
                            ? organizationSignupText
                            : organizationSignIn}
                    </Button>
                </div>
            </div>
        </>
    );
};

export default React.memo(SearchResult);
